import React, { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Eye, Edit, FileText, Trash2, KeyRound, DollarSign, ClipboardList, ChevronDown, ChevronUp, Clock, CreditCard, Banknote, Receipt, MapPin, Users, Calendar, Copy, Sparkles } from 'lucide-react';
import axios from 'axios';
import { getBusinessLine, businessLineColor } from '../../utils/businessLine';
import { parseLocalDate } from '../../utils/dateUtils';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const API = process.env.REACT_APP_BACKEND_URL;

const ContractCard = ({ 
  contract, onPreview, onEdit, onBEO, onMenuPreview, onPackingList, onInvoice,
  onClientAccess, onRecordPayment, onEventBrief, onDuplicate, onStatusChange, onDelete, navigate, eventTypes = [],
  onAIEmail,
}) => {
  const [showPaymentHistory, setShowPaymentHistory] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const fetchPaymentHistory = async () => {
    if (paymentHistory.length > 0) { setShowPaymentHistory(!showPaymentHistory); return; }
    setLoadingHistory(true);
    try {
      const response = await axios.get(`${API}/api/payments/contract/${contract.id}`);
      setPaymentHistory(response.data);
      setShowPaymentHistory(true);
    } catch (error) { console.error('Error:', error); }
    finally { setLoadingHistory(false); }
  };

  const formatPaymentMethod = (m) => ({ cash: 'Cash', check: 'Check', credit_card: 'Credit Card', debit: 'Debit', e_transfer: 'E-Transfer', wire: 'Wire' }[m] || m);
  const formatPaymentType = (t) => ({ deposit: 'Deposit', partial: 'Partial', balance: 'Balance', final: 'Final', full: 'Full', refund: 'Refund' }[t] || t);
  
  const getMethodIcon = (m) => {
    if (m === 'credit_card' || m === 'debit') return <CreditCard size={12} className="text-blue-500" />;
    if (m === 'cash') return <Banknote size={12} className="text-green-500" />;
    if (m === 'e_transfer') return <Receipt size={12} className="text-purple-500" />;
    return <DollarSign size={12} className="text-gray-400" />;
  };

  const statusConfig = {
    paid: { bg: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: 'PAID' },
    deposit_paid: { bg: 'bg-blue-50 text-blue-700 border-blue-200', label: 'DEPOSIT PAID' },
    partial: { bg: 'bg-amber-50 text-amber-700 border-amber-200', label: 'PARTIAL' },
  };
  const payStatus = statusConfig[contract.payment_status] || { bg: 'bg-gray-50 text-gray-600 border-gray-200', label: 'PENDING' };
  const contractStatus = contract.status === 'confirmed';
  const balanceDue = (contract.total_amount || 0) - (contract.total_paid || 0);

  const eventDate = parseLocalDate(contract.event_date) || new Date();
  const isUpcoming = eventDate >= new Date();
  const daysAway = Math.ceil((eventDate - new Date()) / (1000 * 60 * 60 * 24));

  // Event-type colour stripe (from Settings → Event Types; falls back to a sensible default)
  const FALLBACK_COLORS = { onsite:'#9333ea', offsite:'#dc2626', tournament:'#16a34a', cocktail_coach:'#0abab5', pickup:'#1e3a5f', dropoff:'#1e3a5f', celebration_of_life:'#7c3aed', bridal_shower:'#ec4899', baby_shower:'#f59e0b', birthday:'#f97316', rehearsal_dinner:'#8b5cf6', golf_tournament:'#059669', rustic_ranch:'#dc2626' };
  const eventTypeColor = eventTypes.find(t => t.slug === contract.event_type)?.color || FALLBACK_COLORS[contract.event_type] || '#6b7280';

  return (
    <Card data-testid={`contract-card-${contract.id}`} className="border border-gray-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden">
      {/* Event-type colour stripe */}
      <div style={{ backgroundColor: eventTypeColor }} className="h-1.5 w-full" aria-hidden="true" />
      {/* Top Bar */}
      <div className={`flex items-center justify-between px-5 py-3 ${contractStatus ? 'bg-emerald-600' : 'bg-[#1e3a5f]'} text-white`}>
        <div className="flex items-center gap-3">
          <h3 className="font-semibold text-base">{contract.event_name}</h3>
          <span className="px-2 py-0.5 bg-white/20 rounded text-xs capitalize">{contract.event_type?.replace(/_/g, ' ')}</span>
          <span
            data-testid={`business-line-${contract.id}`}
            className={`px-2 py-0.5 rounded text-xs font-semibold border ${businessLineColor(getBusinessLine(contract.event_type))}`}
          >
            {getBusinessLine(contract.event_type)}
          </span>
        </div>
        <div className="flex items-center gap-3 text-sm">
          <span className="opacity-80 font-mono text-xs">{contract.contract_number}</span>
          <Select value={contract.status || 'pending'} onValueChange={(v) => onStatusChange && onStatusChange(contract.id, v)}>
            <SelectTrigger className={`h-6 w-auto px-2 border-0 text-xs font-semibold rounded ${contract.status === 'completed' ? 'bg-white text-gray-600' : contract.status === 'confirmed' ? 'bg-white text-emerald-700' : 'bg-white/20 text-white'}`} data-testid={`status-select-${contract.id}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="confirmed">Confirmed</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <CardContent className="p-5">
        {/* Key Info Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-4">
          <div>
            <p className="text-xs text-gray-400 mb-0.5">Client</p>
            <p className="text-sm font-semibold text-[#1e3a5f] truncate">{contract.client_name}</p>
            <p className="text-xs text-gray-400 truncate">{contract.client_email}</p>
          </div>
          <div>
            <p className="text-xs text-gray-400 mb-0.5">Date</p>
            <p className="text-sm font-semibold text-[#1e3a5f]">{eventDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p>
            <p className="text-xs text-gray-400">{contract.event_time}{isUpcoming && daysAway <= 30 ? ` (${daysAway}d)` : ''}</p>
          </div>
          <div>
            <p className="text-xs text-gray-400 mb-0.5">Location</p>
            <p className="text-sm text-[#1e3a5f] truncate">{contract.location}</p>
            <p className="text-xs text-gray-400 capitalize">{contract.location_type}</p>
          </div>
          <div>
            <p className="text-xs text-gray-400 mb-0.5">Guests</p>
            <p className="text-sm font-semibold text-[#1e3a5f]">{contract.guest_count}</p>
            {contract.service_style && <p className="text-xs text-gray-400 capitalize">{contract.service_style?.replace(/_/g, ' ')}</p>}
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-400 mb-0.5">Total</p>
            <p className="text-lg font-bold text-[#1e3a5f]">${contract.total_amount?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
            {contract.gratuity_amount > 0 && (
              <p className="text-xs text-gray-400">incl. {(contract.gratuity_rate * 100).toFixed(0)}% gratuity</p>
            )}
          </div>
        </div>

        {/* Payment Status Row */}
        <div className="flex items-center gap-3 mb-4 pb-4 border-b border-gray-100">
          <span className={`inline-flex items-center px-2.5 py-1 rounded text-xs font-semibold border ${payStatus.bg}`}>
            {payStatus.label}
          </span>
          {contract.total_paid > 0 && (
            <span className="text-xs text-gray-500">
              ${contract.total_paid?.toLocaleString()} paid &middot; ${balanceDue.toLocaleString()} remaining
            </span>
          )}
          {contract.total_paid > 0 && (
            <button onClick={fetchPaymentHistory}
              className="text-xs text-[#1e3a5f] hover:underline ml-auto flex items-center gap-1"
              data-testid={`payment-history-btn-${contract.id}`}>
              {loadingHistory ? 'Loading...' : showPaymentHistory ? 'Hide' : 'History'}
              {showPaymentHistory ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            </button>
          )}
        </div>

        {/* Payment History */}
        {showPaymentHistory && paymentHistory.length > 0 && (
          <div className="mb-4 bg-gray-50 rounded p-3 text-sm" data-testid={`payment-history-${contract.id}`}>
            <div className="space-y-1.5">
              {paymentHistory.map((p, i) => (
                <div key={p.id || i} className="flex items-center justify-between bg-white rounded px-3 py-2 border border-gray-100">
                  <div className="flex items-center gap-2">
                    {getMethodIcon(p.payment_method)}
                    <span className="font-semibold text-green-700">${p.amount?.toLocaleString()}</span>
                    <span className="text-gray-400">|</span>
                    <span className="text-gray-600 text-xs">{formatPaymentType(p.payment_type)} via {formatPaymentMethod(p.payment_method)}</span>
                  </div>
                  <span className="text-xs text-gray-400">
                    {p.created_at ? new Date(p.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => onEdit(contract)} data-testid={`edit-contract-btn-${contract.id}`}
            variant="outline" size="sm" className="text-xs border-[#1e3a5f] text-[#1e3a5f] hover:bg-[#1e3a5f] hover:text-white">
            <Edit size={14} className="mr-1" /> Edit
          </Button>
          <Button onClick={() => onPreview(contract)} data-testid={`preview-contract-btn-${contract.id}`}
            size="sm" className="text-xs bg-[#c9a227] hover:bg-[#b08f1f] text-white font-semibold">
            <Eye size={14} className="mr-1" /> Preview
          </Button>
          <Button onClick={() => onBEO(contract)} data-testid={`view-beo-btn-${contract.id}`}
            size="sm" className="text-xs bg-[#1e3a5f] hover:bg-[#15304f] text-white">
            <FileText size={14} className="mr-1" /> BEO
          </Button>
          <Button onClick={() => onMenuPreview(contract)} data-testid={`menu-preview-btn-${contract.id}`}
            variant="outline" size="sm" className="text-xs border-gray-300 text-gray-600 hover:bg-gray-100">
            Menu Card
          </Button>
          {contract.location_type === 'offsite' && (
            <Button onClick={() => navigate('/packing', { state: { contract } })} data-testid={`packing-list-btn-${contract.id}`}
              variant="outline" size="sm" className="text-xs border-gray-300 text-gray-600 hover:bg-gray-100">
              Packing List
            </Button>
          )}
          <Button onClick={() => onInvoice(contract)} data-testid={`invoice-btn-${contract.id}`}
            variant="outline" size="sm" className="text-xs border-green-600 text-green-700 hover:bg-green-600 hover:text-white">
            <Receipt size={14} className="mr-1" /> Invoice
          </Button>
          <div className="ml-auto flex gap-2">
            <Button onClick={() => onRecordPayment(contract)} data-testid={`record-payment-btn-${contract.id}`}
              variant="outline" size="sm" className="text-xs border-green-600 text-green-600 hover:bg-green-600 hover:text-white">
              <DollarSign size={14} className="mr-1" /> Payment
            </Button>
            <Button onClick={() => onClientAccess(contract)} data-testid={`client-access-btn-${contract.id}`}
              variant="outline" size="sm" className="text-xs border-blue-500 text-blue-500 hover:bg-blue-500 hover:text-white">
              <KeyRound size={14} className="mr-1" /> Access
            </Button>
            <Button onClick={() => onEventBrief(contract)} data-testid={`event-brief-btn-${contract.id}`}
              variant="outline" size="sm" className="text-xs border-purple-500 text-purple-500 hover:bg-purple-500 hover:text-white">
              <ClipboardList size={14} className="mr-1" /> Brief
            </Button>
            {onAIEmail && (
              <Button onClick={() => onAIEmail(contract)} data-testid={`ai-email-btn-${contract.id}`}
                variant="outline" size="sm" className="text-xs border-amber-500 text-amber-600 hover:bg-amber-500 hover:text-white">
                <Sparkles size={14} className="mr-1" /> AI Email
              </Button>
            )}
            <Button onClick={() => onDuplicate(contract)} data-testid={`duplicate-contract-btn-${contract.id}`}
              variant="outline" size="sm" className="text-xs border-gray-400 text-gray-600 hover:bg-gray-600 hover:text-white">
              <Copy size={14} className="mr-1" /> Duplicate
            </Button>
            <Button onClick={(e) => { e.stopPropagation(); onDelete(contract.id); }} data-testid={`delete-contract-btn-${contract.id}`}
              variant="ghost" size="sm" className="text-xs text-red-400 hover:text-red-600 hover:bg-red-50">
              <Trash2 size={14} />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ContractCard;
